import { useSearchParams } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import {
  ResponsiveContainer, AreaChart, Area,
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend
} from 'recharts';
import { collection, addDoc, getDocs, query, where, updateDoc, doc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import { motion, AnimatePresence } from "framer-motion";
import { examLessons } from "../config/ExamConfig";
import { toast } from "sonner";
import { FiEdit2, FiTrash2, FiPlus, FiSettings, FiActivity, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import '../styles/TrialAnalysisPage.css';

function TrialAnalysisPage() {
  const [searchParams] = useSearchParams();
  const exam = searchParams.get('exam') || 'TYT–AYT';
  const userId = localStorage.getItem("userId");
  const username = localStorage.getItem("username") || "Misafir";

  const [activeTab, setActiveTab] = useState("genel");
  const [genelDersler, setGenelDersler] = useState(Array.isArray(examLessons[exam]) ? examLessons[exam] : []);
  const [bransDersler, setBransDersler] = useState(Array.isArray(examLessons[exam]) ? examLessons[exam].slice(0, 1) : [{ name: 'Branş Dersi', totalQuestions: 40 }]);
  
  const [genelEntries, setGenelEntries] = useState([]);
  const [bransEntries, setBransEntries] = useState([]);
  const [netler, setNetler] = useState({});
  const [dogruYanlis, setDogruYanlis] = useState({});
  const [yorum, setYorum] = useState({ zor: '', neden: '', telafi: '', tarih: '', his: '', yayin: '' });
  
  const [editingId, setEditingId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Modals & UI States
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [newLesson, setNewLesson] = useState({ name: '', totalQuestions: '' });
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [selectedBransChartLesson, setSelectedBransChartLesson] = useState(null);

  // Fetch data
  useEffect(() => {
    if (!userId) return;
    const fetchEntries = async () => {
      try {
        const q = query(collection(db, "denemeler"), where("userId", "==", userId));
        const querySnapshot = await getDocs(q);
        const genel = [];
        const brans = [];
        querySnapshot.forEach((doc) => {
          const data = { id: doc.id, ...doc.data() };
          data.dersler = Array.isArray(data.dersler) ? data.dersler : [];
          if (data.type === "genel") genel.push(data);
          else if (data.type === "brans") brans.push(data);
        });
        setGenelEntries(genel);
        setBransEntries(brans);
      } catch (err) {
        toast.error(`Veriler çekilemedi: ${err.message}`);
      }
    };
    fetchEntries();
  }, [userId]);

  // Init form fields when tab or lessons change
  useEffect(() => {
    const dersler = activeTab === "genel" ? genelDersler : bransDersler;
    const initialNetler = {};
    const initialDogruYanlis = {};
    dersler.forEach((ders) => {
      initialNetler[ders.name] = '';
      initialDogruYanlis[ders.name] = { dogru: '', yanlis: '' };
    });
    // Only reset if not editing
    if (!editingId) {
      setNetler(initialNetler);
      setDogruYanlis(initialDogruYanlis);
    }
  }, [activeTab, genelDersler, bransDersler, editingId]);

  // Pre-select first brans lesson for charts
  useEffect(() => {
    if (activeTab === "brans" && !selectedBransChartLesson && bransDersler.length > 0) {
      setSelectedBransChartLesson(bransDersler[0].name);
    }
  }, [activeTab, bransDersler, selectedBransChartLesson]);


  // Handlers
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setEditingId(null);
    setYorum({ zor: '', neden: '', telafi: '', tarih: '', his: '', yayin: '' });
    setExpandedEntry(null);
  };

  const handleYorumChange = (e) => {
    const { name, value } = e.target;
    setYorum(prev => ({ ...prev, [name]: value }));
  };

  const handleNetChange = (e, dersName) => {
    const value = e.target.value;
    setNetler(prev => ({ ...prev, [dersName]: value }));
  };

  const handleDogruYanlisChange = (e, dersName, field) => {
    const value = e.target.value;
    setDogruYanlis(prev => ({ ...prev, [dersName]: { ...prev[dersName], [field]: value } }));
  };

  const handleLessonChange = (index, field, value) => {
    const isGenel = activeTab === "genel";
    const dersler = isGenel ? [...genelDersler] : [...bransDersler];
    dersler[index][field] = field === 'totalQuestions' ? Number(value) : value;
    isGenel ? setGenelDersler(dersler) : setBransDersler(dersler);
  };

  const handleAddLesson = () => {
    if (!newLesson.name || !newLesson.totalQuestions) {
      toast.error("Ders adı ve soru sayısı boş olamaz!");
      return;
    }
    const isGenel = activeTab === "genel";
    const updatedDersler = isGenel ? [...genelDersler] : [...bransDersler];
    updatedDersler.push({ name: newLesson.name, totalQuestions: Number(newLesson.totalQuestions) });
    isGenel ? setGenelDersler(updatedDersler) : setBransDersler(updatedDersler);
    setNewLesson({ name: '', totalQuestions: '' });
    setShowLessonModal(false);
  };

  const handleRemoveLesson = (index) => {
    const isGenel = activeTab === "genel";
    const dersler = isGenel ? [...genelDersler] : [...bransDersler];
    const dersName = dersler[index].name;
    const updated = dersler.filter((_, i) => i !== index);
    isGenel ? setGenelDersler(updated) : setBransDersler(updated);

    // Clean up state
    const updatedNetler = { ...netler };
    const updatedDogruYanlis = { ...dogruYanlis };
    delete updatedNetler[dersName];
    delete updatedDogruYanlis[dersName];
    setNetler(updatedNetler);
    setDogruYanlis(updatedDogruYanlis);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const isGenel = activeTab === "genel";
    const dersler = isGenel ? genelDersler : bransDersler;
    const toplamNet = Object.values(netler).reduce((acc, val) => acc + Number(val || 0), 0);
    
    const yeniKayit = {
      userId, examType: exam, type: activeTab,
      dersler: dersler.map(ders => ({ name: ders.name, totalQuestions: ders.totalQuestions })),
      netler: { ...netler }, dogruYanlis: { ...dogruYanlis },
      toplamNet, yorumlar: { ...yorum }, timestamp: new Date(),
    };

    try {
      if (editingId) {
        await updateDoc(doc(db, "denemeler", editingId), yeniKayit);
        const updateList = list => list.map(entry => entry.id === editingId ? { id: editingId, ...yeniKayit } : entry);
        isGenel ? setGenelEntries(updateList(genelEntries)) : setBransEntries(updateList(bransEntries));
        setEditingId(null);
        toast.success("Kayıt güncellendi!");
      } else {
        const docRef = await addDoc(collection(db, "denemeler"), yeniKayit);
        const newEntry = { id: docRef.id, ...yeniKayit };
        isGenel ? setGenelEntries([...genelEntries, newEntry]) : setBransEntries([...bransEntries, newEntry]);
        toast.success("Kayıt başarıyla eklendi! 🎉");
      }
      setYorum({ zor: '', neden: '', telafi: '', tarih: '', his: '', yayin: '' });
      // Netler resetting is handled by useEffect
      setEditingId(null);
    } catch (err) {
      toast.error(`Kayıt sırasında hata: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (id) => {
    const isGenel = activeTab === "genel";
    const entries = isGenel ? genelEntries : bransEntries;
    const selected = entries.find((e) => e.id === id);
    if (selected) {
      if (isGenel) {
        setGenelDersler(Array.isArray(selected.dersler) ? selected.dersler : examLessons[exam] || []);
      } else {
        setBransDersler(Array.isArray(selected.dersler) ? selected.dersler : examLessons[exam]?.slice(0, 1) || [{ name: 'Branş', totalQuestions: 40 }]);
      }
      setNetler(selected.netler || {});
      setDogruYanlis(selected.dogruYanlis || {});
      setYorum(selected.yorumlar || { zor: '', neden: '', telafi: '', tarih: '', his: '', yayin: '' });
      setEditingId(id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu kaydı silmek istediğine emin misin?")) return;
    try {
      await deleteDoc(doc(db, "denemeler", id));
      const isGenel = activeTab === "genel";
      if (isGenel) setGenelEntries(genelEntries.filter((e) => e.id !== id));
      else setBransEntries(bransEntries.filter((e) => e.id !== id));
      toast.success("Kayıt silindi.");
    } catch (err) {
      toast.error(`Silme hatası: ${err.message}`);
    }
  };

  // Process data for charts & lists
  const formatDateTime = (dateInput) => {
    let date;
    if (dateInput?.toDate) date = dateInput.toDate();
    else if (typeof dateInput === "string") date = new Date(dateInput);
    else if (dateInput instanceof Date) date = dateInput;
    else return "-";
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("tr-TR");
  };

  const entries = activeTab === "genel" ? genelEntries : bransEntries;
  const sortedEntries = [...entries].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  const sortedEntriesChronological = [...entries].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  // Chart Data: Overall Trend
  const overallChartData = useMemo(() => {
    return sortedEntriesChronological.map((entry) => ({
      name: entry.yorumlar?.tarih || formatDateTime(entry.timestamp),
      net: entry.toplamNet || 0,
      yayin: entry.yorumlar?.yayin || 'Bilinmiyor'
    }));
  }, [sortedEntriesChronological]);

  // Chart Data: Subject Trend (only for Brans)
  const subjectChartData = useMemo(() => {
    if (activeTab !== "brans" || !selectedBransChartLesson) return [];
    // Filter entries that have this subject
    const entriesWithSubject = sortedEntriesChronological.filter(e => 
      e.dersler?.some(d => d.name === selectedBransChartLesson)
    );
    return entriesWithSubject.map(entry => ({
      name: entry.yorumlar?.tarih || formatDateTime(entry.timestamp),
      net: Number(entry.netler?.[selectedBransChartLesson] || 0),
      yayin: entry.yorumlar?.yayin || 'Bilinmiyor'
    }));
  }, [sortedEntriesChronological, activeTab, selectedBransChartLesson]);


  // Custom Tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-recharts-tooltip">
          <p className="label">{label}</p>
          <p style={{ color: payload[0].color, fontWeight: 800 }}>Net: {payload[0].value}</p>
          <p style={{ color: '#94a3b8', fontSize: '11px', marginTop: '6px' }}>Yayın: {payload[0].payload.yayin}</p>
        </div>
      );
    }
    return null;
  };

  const dersler = activeTab === "genel" ? genelDersler : bransDersler;

  return (
    <div className="trial-page">
      <div className="trial-content-wrapper">
        
        {/* Header */}
        <motion.div className="trial-header" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="trial-title">Sınav <span className="text-gradient">Analiz Merkezi</span></h1>
          <p className="trial-subtitle">Deneme sonuçlarını kaydet, zayıf noktalarını tespit et ve hedefine odaklan.</p>
        </motion.div>

        {/* Tabs */}
        <motion.div className="trial-tabs" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <button className={`trial-tab-btn ${activeTab === 'genel' ? 'active' : ''}`} onClick={() => handleTabChange('genel')}>
            🌍 Genel Denemeler
          </button>
          <button className={`trial-tab-btn ${activeTab === 'brans' ? 'active' : ''}`} onClick={() => handleTabChange('brans')}>
            📘 Branş Denemeleri
          </button>
        </motion.div>

        {/* Top Section: Charts */}
        {sortedEntries.length > 0 && (
          <motion.div className="charts-grid" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            <div className="chart-card">
              <div className="chart-header">
                <span className="chart-title"><FiActivity style={{ marginRight: 8 }} /> Gelişim Grafiği (Toplam Net)</span>
              </div>
              <div style={{ width: '100%', height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={overallChartData}>
                    <defs>
                      <linearGradient id="colorNet" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.4}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                    <XAxis dataKey="name" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="net" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorNet)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* If Brans, show secondary chart for specific lesson */}
            {activeTab === "brans" && subjectChartData.length > 0 && (
              <div className="chart-card">
                <div className="chart-header" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 12 }}>
                  <span className="chart-title"><FiPieChart style={{ marginRight: 8 }} /> Ders Analizi</span>
                  <select 
                    className="trial-input" 
                    style={{ width: '100%', padding: '8px' }}
                    value={selectedBransChartLesson || ''}
                    onChange={(e) => setSelectedBransChartLesson(e.target.value)}
                  >
                    {bransDersler.map(d => (
                      <option key={d.name} value={d.name}>{d.name}</option>
                    ))}
                  </select>
                </div>
                <div style={{ width: '100%', height: 200, marginTop: 'auto' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={subjectChartData}>
                      <defs>
                        <linearGradient id="barColor" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#3b82f6" />
                          <stop offset="100%" stopColor="#2563eb" />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                      <XAxis dataKey="name" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} />
                      <Tooltip content={<CustomTooltip />} cursor={{fill: 'rgba(255,255,255,0.02)'}} />
                      <Bar dataKey="net" fill="url(#barColor)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Main Grid: Form (Left) & List (Right) */}
        <div className="trial-grid">
          {/* Data Entry Form */}
          <motion.div className="trial-glass-card" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}>
            <div className="trial-form-header">
              <h2 className="trial-form-title">
                {editingId ? '✏️ Kaydı Düzenle' : '✨ Yeni Kayıt Ekle'}
              </h2>
              <button className="btn-secondary" onClick={() => setShowLessonModal(true)}>
                <FiSettings size={14} style={{ marginRight: 6 }} /> Dersleri Yönet
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="input-group" style={{ marginBottom: 20 }}>
                <label className="input-label">Tarih</label>
                <input type="date" name="tarih" value={yorum.tarih} onChange={handleYorumChange} className="trial-input" required />
              </div>
              <div className="input-group" style={{ marginBottom: 20 }}>
                <label className="input-label">Yayın Adı</label>
                <input type="text" name="yayin" value={yorum.yayin} onChange={handleYorumChange} className="trial-input" placeholder="Örn: 3D, Bilgi Sarmal..." required />
              </div>

              <h4 style={{ color: '#10b981', margin: '24px 0 16px', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Ders Performansı</h4>
              
              {dersler.map((ders, i) => (
                <div key={ders.name + i} className="lesson-card">
                  <div className="lesson-card-header">
                    <span className="lesson-title">{ders.name}</span>
                    <span style={{ fontSize: 12, color: '#64748b' }}>{ders.totalQuestions} Soru</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                    <div className="input-group">
                      <label className="input-label">Doğru</label>
                      <input type="number" min="0" max={ders.totalQuestions} className="trial-input" style={{ textAlign: 'center' }}
                        value={dogruYanlis[ders.name]?.dogru || ''} onChange={(e) => handleDogruYanlisChange(e, ders.name, 'dogru')} />
                    </div>
                    <div className="input-group">
                      <label className="input-label">Yanlış</label>
                      <input type="number" min="0" max={ders.totalQuestions} className="trial-input" style={{ textAlign: 'center' }}
                        value={dogruYanlis[ders.name]?.yanlis || ''} onChange={(e) => handleDogruYanlisChange(e, ders.name, 'yanlis')} />
                    </div>
                    <div className="input-group">
                      <label className="input-label">Net</label>
                      <input type="number" step="0.25" className="trial-input" style={{ textAlign: 'center', borderColor: '#3b82f6' }} required
                        value={netler[ders.name] || ''} onChange={(e) => handleNetChange(e, ders.name)} />
                    </div>
                  </div>
                </div>
              ))}

              <h4 style={{ color: '#10b981', margin: '32px 0 16px', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Analiz & Yorumlar</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="input-group">
                  <label className="input-label">Zorlandığın Konular</label>
                  <textarea name="zor" value={yorum.zor} onChange={handleYorumChange} className="trial-input" placeholder="Hangi konularda takıldın?" />
                </div>
                <div className="input-group">
                  <label className="input-label">Hataların Nedeni</label>
                  <textarea name="neden" value={yorum.neden} onChange={handleYorumChange} className="trial-input" placeholder="Dikkatsizlik mi, bilgi eksikliği mi?" />
                </div>
                <div className="input-group">
                  <label className="input-label">Telafi Planı</label>
                  <textarea name="telafi" value={yorum.telafi} onChange={handleYorumChange} className="trial-input" placeholder="Bu hataları nasıl telafi edeceksin?" />
                </div>
              </div>

              <button type="submit" className="btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Kaydediliyor...' : (editingId ? 'Güncelle' : 'Kaydet')}
              </button>
              {editingId && (
                <button type="button" className="btn-secondary" style={{ width: '100%', marginTop: 12 }} onClick={() => setEditingId(null)}>
                  İptal Et
                </button>
              )}
            </form>
          </motion.div>

          {/* Entries List */}
          <motion.div className="trial-glass-card" style={{ background: 'transparent', border: 'none', boxShadow: 'none', padding: 0 }} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
              📋 {activeTab === 'genel' ? 'Genel Deneme' : 'Branş Denemesi'} Geçmişi
            </h3>
            
            <AnimatePresence>
              {sortedEntries.length === 0 ? (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ padding: 40, textAlign: 'center', color: '#64748b', background: 'rgba(255,255,255,0.02)', borderRadius: 20 }}>
                  Henüz kayıt bulunmuyor.
                </motion.div>
              ) : (
                sortedEntries.map((entry) => (
                  <motion.div key={entry.id} className="entry-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} layout>
                    <div className="entry-header" onClick={() => setExpandedEntry(expandedEntry === entry.id ? null : entry.id)}>
                      <div>
                        <div className="entry-title">{entry.yorumlar?.yayin || 'Yayın Belirtilmemiş'}</div>
                        <div className="entry-date">{entry.yorumlar?.tarih || formatDateTime(entry.timestamp)}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <span className="entry-net-badge">{entry.toplamNet} Net</span>
                        {expandedEntry === entry.id ? <FiChevronUp color="#94a3b8" /> : <FiChevronDown color="#94a3b8" />}
                      </div>
                    </div>
                    
                    <AnimatePresence>
                      {expandedEntry === entry.id && (
                        <motion.div className="entry-content" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                          <div className="entry-details-grid">
                            {(Array.isArray(entry.dersler) ? entry.dersler : []).map((ders, i) => (
                              <div key={i} className="entry-detail-item">
                                <div className="entry-detail-label">{ders.name}</div>
                                <div className="entry-detail-value">{entry.netler?.[ders.name] || 0} N</div>
                                <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>
                                  {entry.dogruYanlis?.[ders.name]?.dogru || 0} D / {entry.dogruYanlis?.[ders.name]?.yanlis || 0} Y
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {(entry.yorumlar?.zor || entry.yorumlar?.neden || entry.yorumlar?.telafi) && (
                            <div className="entry-comments">
                              {entry.yorumlar.zor && <div className="entry-comment-item"><strong>Zorlanılan:</strong> {entry.yorumlar.zor}</div>}
                              {entry.yorumlar.neden && <div className="entry-comment-item"><strong>Hata Nedeni:</strong> {entry.yorumlar.neden}</div>}
                              {entry.yorumlar.telafi && <div className="entry-comment-item"><strong>Telafi Planı:</strong> {entry.yorumlar.telafi}</div>}
                            </div>
                          )}

                          <div className="entry-actions">
                            <button className="btn-secondary" onClick={() => handleEdit(entry.id)}>
                              <FiEdit2 size={14} style={{ marginRight: 6 }} /> Düzenle
                            </button>
                            <button className="btn-danger" onClick={() => handleDelete(entry.id)}>
                              <FiTrash2 size={14} style={{ marginRight: 6 }} /> Sil
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>

      {/* Lesson Manage Modal */}
      <AnimatePresence>
        {showLessonModal && (
          <div className="modal-overlay" onClick={() => setShowLessonModal(false)}>
            <motion.div className="trial-modal" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} onClick={e => e.stopPropagation()}>
              <h2 className="modal-title">Dersleri Yönet</h2>
              <div style={{ marginBottom: 24 }}>
                {dersler.map((ders, index) => (
                  <div key={index} style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center' }}>
                    <input type="text" className="trial-input" style={{ flex: 2 }} value={ders.name} onChange={(e) => handleLessonChange(index, 'name', e.target.value)} placeholder="Ders Adı" />
                    <input type="number" className="trial-input" style={{ flex: 1 }} value={ders.totalQuestions} onChange={(e) => handleLessonChange(index, 'totalQuestions', e.target.value)} placeholder="Soru Sayısı" />
                    <button className="btn-danger" style={{ padding: '12px' }} onClick={() => handleRemoveLesson(index)}>
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
              <div style={{ padding: 16, background: 'rgba(255,255,255,0.03)', borderRadius: 16, marginBottom: 24 }}>
                <h4 style={{ fontSize: 13, color: '#10b981', marginBottom: 12 }}>Yeni Ders Ekle</h4>
                <div style={{ display: 'flex', gap: 12 }}>
                  <input type="text" className="trial-input" style={{ flex: 2 }} value={newLesson.name} onChange={(e) => setNewLesson({ ...newLesson, name: e.target.value })} placeholder="Örn: Tarih" />
                  <input type="number" className="trial-input" style={{ flex: 1 }} value={newLesson.totalQuestions} onChange={(e) => setNewLesson({ ...newLesson, totalQuestions: e.target.value })} placeholder="Soru" />
                  <button className="btn-secondary" style={{ background: '#10b981', color: '#fff', border: 'none' }} onClick={handleAddLesson}>
                    Ekle
                  </button>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button className="btn-primary" style={{ width: 'auto', marginTop: 0 }} onClick={() => setShowLessonModal(false)}>Tamam</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}

export default TrialAnalysisPage;